<template>
  <div>
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-baseline mb-2">
      <back-with-title
        :title="$t('Milestones')"
        :route-name="'project-board'"
        :params="{ boardUid: $route.params.boardUid }"
      />

      <div class="w-25 text-right">
        <b-button
          v-b-modal.create-edit-milestone-modal
          variant="primary"
        >
          <feather-icon
            icon="PlusIcon"
            size="16"
            class="mr-50"
          />
          {{ $t('Add Milestone') }}
        </b-button>
      </div>
    </div>

    <!-- Milestones Table -->
    <b-overlay :show="fetchingBoard">
      <b-table
        :items="milestones"
        :fields="fields"
        responsive
        show-empty
      >
        <template #head()="{ label }">
          <span class="text-capitalize">{{ label }}</span>
        </template>

        <template #cell(description)="{ item }">
          <span>{{ item.description || '-' }}</span>
        </template>

        <template #cell(deadline)="{ item }">
          <span>{{ item.deadline ? FORMAT_DATE(item.deadline, 'MMM D, YYYY') : '-' }}</span>
        </template>

        <template #cell(actions)="{ item }">
          <b-button
            v-b-tooltip.hover="'View Tasks'"
            size="sm"
            variant="flat-primary"
            @click="$router.push({
              name: 'project-board-milestone',
              params: { milestoneUid: base58Converter(item.uuid), boardUid: $route.params.boardUid }
            })"
          >
            <feather-icon icon="ClipboardIcon" />
          </b-button>

          <b-button
            v-if="canManageBoards"
            v-b-modal.create-edit-milestone-modal
            v-b-tooltip.hover="'Edit'"
            size="sm"
            variant="flat-warning"
            @click="selectedMilestone = { ...item }"
          >
            <feather-icon icon="EditIcon" />
          </b-button>
          <b-button
            v-if="canManageBoards"
            v-b-modal.confirm-delete-milestone-modal
            v-b-tooltip.hover="'Delete'"
            size="sm"
            variant="flat-danger"
            @click="selectedMilestone = { ...item }"
          >
            <feather-icon icon="Trash2Icon" />
          </b-button>
        </template>
      </b-table>
    </b-overlay>

    <!-- Add / Edit Milestone Modal -->
    <add-milestone
      :milestone="selectedMilestone"
      @refresh="fetchBoard({ uuid: boardUid })"
      @updated="selectedMilestone = null"
    />

    <!-- Delete Milestone Confirmation Modal -->
    <confirm-modal
      v-if="selectedMilestone"
      modal="confirm-delete-milestone-modal"
      :title="$t('Delete Milestone')"
      ok-variant="danger"
      :alert="`Are you sure you want to delete the milestone '${selectedMilestone.name}'?`"
      alert-variant="danger"
      @confirm="onDeleteConfirm"
    />
  </div>
</template>

<script>
import { BButton, BTable, BOverlay } from 'bootstrap-vue'
import ConfirmModal from '@/views/Wallet/components/reusables/ConfirmModal.vue'
import BackWithTitle from '@/views/common/components/BackWithTitle.vue'
import { mapActions, mapGetters } from 'vuex'
import AddMilestone from './AddMilestone.vue'

export default {
  components: {
    BButton,
    BTable,
    BOverlay,
    AddMilestone,
    ConfirmModal,
    BackWithTitle,
  },
  data() {
    return {
      fields: [
        { key: 'name', label: this.$t('Name') },
        { key: 'description', label: this.$t('Description') },
        { key: 'deadline', label: this.$t('Deadline') },
        { key: 'actions', label: this.$t('Actions'), class: 'text-center' },
      ],
      selectedMilestone: null,
    }
  },
  computed: {
    ...mapGetters('boards', {
      milestones: 'GET_CURRENT_BOARD_MILESTONES',
      fetchingBoard: 'GET_FETCHING_BOARD',
    }),
    ...mapGetters('permissions', ['canManageBoards']),
    boardUid() {
      return this.base58Converter(this.$route.params.boardUid, false)
    },
  },
  async mounted() {
    if (!this.milestones?.length) {
      await this.fetchBoard({ uuid: this.boardUid })
    }
  },
  methods: {
    ...mapActions('boards', ['fetchBoard', 'deleteBoardMilestone']),
    async onDeleteConfirm() {
      try {
        const response = await this.deleteBoardMilestone({
          projectUid: this.$store.state.project.selectedProject,
          boardUid: this.$route.params.boardUid,
          uuid: this.base58Converter(this.selectedMilestone.uuid),
        })
        this.showSuccess(response.message)
      } catch (error) {
        this.showError(error.message)
      } finally {
        this.selectedMilestone = null
      }
    },
  },
}
</script>
